import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAddToast } from '@/components/Snackbar/Snackbar';

import {
	useInlineEditContext,
	type DraftFieldChange
} from '../InlineEditContext';

type SubmitResult = { ok: true } | { ok: false; error: string };

type SubmitFn = (
	changes: DraftFieldChange[],
	notes: string | undefined,
	thirdPartyConfirmed: boolean,
	attachmentIds: string[]
) => Promise<SubmitResult>;

export const useFieldChangeSubmit = (
	activeKey: string,
	submit: SubmitFn,
	onSuccess?: () => void
) => {
	const ctx = useInlineEditContext();
	const { t } = useTranslation();
	const addToast = useAddToast();

	const [pendingChanges, setPendingChanges] = useState<
		DraftFieldChange[] | null
	>(null);
	const [submitting, setSubmitting] = useState(false);

	const cancelSubmit = useCallback(() => setPendingChanges(null), []);

	const confirmSubmit = useCallback(
		async (
			notes: string | undefined,
			thirdPartyConfirmed: boolean,
			attachmentIds: string[]
		) => {
			if (!ctx || !pendingChanges || pendingChanges.length === 0) return;

			setSubmitting(true);
			const result = await submit(
				pendingChanges,
				notes,
				thirdPartyConfirmed,
				attachmentIds
			);
			setSubmitting(false);
			setPendingChanges(null);

			if (result.ok) {
				ctx.requestDeactivate(activeKey);
				addToast(t('Change submitted for review'), undefined, 'success');
				onSuccess?.();
			}
		},
		[activeKey, addToast, ctx, onSuccess, pendingChanges, submit, t]
	);

	return {
		pendingChanges,
		setPendingChanges,
		submitting,
		cancelSubmit,
		confirmSubmit
	};
};
